/**
 * Excluir a Empresa, no fim da ficha (13.12, FR-89).
 *
 * **Excluir esconde, e não apaga** (opção C do Gustavo): a Empresa, as Pessoas e os Negócios dela
 * somem de todas as telas, para todo perfil, e os ganhos continuam contando nos relatórios. Voltar
 * uma excluída é ação de operador (PA-7), sem tela.
 *
 * **O botão é conveniência (D-7).** Ele aparece a quem tem `excluir_empresa` (todos os perfis desde
 * 22/10/2026) **e** é a Responsável ou a gestão (escopo `todas`; desde a 13.14b, pelo
 * `quem_le_e_o_dono` da resposta); quem vê a ficha só pela exceção —
 * Responsável de um Negócio dela, Tarefa num Negócio dela, outra filial do Grupo — não o vê, e o
 * serviço responde `404` a eles de qualquer forma. Quem recusa é o serviço.
 *
 * **A confirmação conta antes do sim.** Ela lê a prévia (`GET /empresas/{id}/exclusao`) a cada
 * abertura e pergunta com o número de Negócios ("Você tem certeza? Você apagará X negócios."); à
 * gestão, diz quantos estão em andamento com outras pessoas. Com o bloqueio da PA-5 (o Negócio em
 * andamento de outra pessoa, para quem não é gestão) a mensagem do serviço vai verbatim, e não há
 * "Sim, excluir". O `409` que chegue depois da prévia (o Negócio que nasceu entre as duas) também
 * vai verbatim, e a ficha fica.
 *
 * **O sim sai da ficha antes de esquecê-la**: `replace` para a lista (o Voltar não reabre a ficha do
 * cache — ele a relê e mostra a recusa), a ficha e as Pessoas fora do cache, e o aviso "Empresa
 * excluída." pela região do layout, que sobrevive à troca de tela.
 *
 * **Paridade de celular (NFR-1):** o mesmo botão e o mesmo diálogo nas duas larguras, sem `@media`.
 */

import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

import { useSessao } from '@/modulos/identidade/sessao'
import { Dialogo } from '@/modulos/negocios/Dialogo'
import { anunciar } from '@/modulos/negocios/avisos'
import { CAMINHOS } from '@/shell/navegacao'
import {
  type PreviaDaExclusao,
  useEmpresa,
  useEsquecerAEmpresa,
  useExcluirEmpresa,
  usePreviaDaExclusao,
} from './api'
import { nomeExibidoDaEmpresa } from './nome-da-empresa'
import './exclusao-da-empresa.css'

export const EXCLUIR_EMPRESA = 'Excluir empresa'
export const CONTANDO = 'Contando os negócios…'
export const SIM_EXCLUIR = 'Sim, excluir'
export const EXCLUINDO = 'Excluindo…'
export const FECHAR = 'Fechar'
export const EMPRESA_EXCLUIDA = 'Empresa excluída.'
export const AVISO_DA_EXCLUSAO =
  'A empresa, as pessoas e os negócios dela somem de todas as telas. Os negócios ganhos ' +
  'continuam contando nos relatórios.'

/** A pergunta do Gustavo (D-6): o plural para 2 ou mais, o singular para 1, e a frase do zero. */
export function perguntaDaExclusao(negocios: number): string {
  if (negocios === 0) {
    return 'Você tem certeza? Esta empresa não tem negócios.'
  }
  return negocios === 1
    ? 'Você tem certeza? Você apagará 1 negócio.'
    : `Você tem certeza? Você apagará ${String(negocios)} negócios.`
}

/** À gestão (D-6): quantos dos Negócios estão em andamento com outras pessoas. */
export function deOutrasPessoas(quantos: number): string {
  return quantos === 1
    ? '1 deles está em andamento com outra pessoa.'
    : `${String(quantos)} deles estão em andamento com outras pessoas.`
}

export interface PropriedadesDaExclusao {
  empresaId: string
}

export function ExclusaoDaEmpresa({ empresaId }: PropriedadesDaExclusao) {
  const { data: sessao } = useSessao()
  const empresa = useEmpresa(empresaId)
  const [aberto, setAberto] = useState(false)

  const ficha = empresa.data
  /* Só a ficha **sem dado** esconde o botão (a primeira leitura em erro). Uma releitura que falhe
     depois — o foco da janela durante o `DELETE` — mantém o dado e não desmonta a confirmação: sem
     isso, o `onSuccess` do `mutate` (sair, esquecer, anunciar) nunca rodaria. */
  if (ficha === undefined || sessao === undefined) {
    return null
  }
  //: 13.14b (DW-438): quem é dono (a Responsável ou a gestão) diz a resposta — `quem_le_e_o_dono`.
  const pode = 'excluir_empresa' in sessao.permissoes && ficha.quem_le_e_o_dono
  if (!pode) {
    return null
  }

  return (
    <section className="exclusao-da-empresa" aria-label={EXCLUIR_EMPRESA}>
      <button
        type="button"
        className="exclusao-da-empresa__botao"
        disabled={aberto}
        onClick={() => {
          setAberto(true)
        }}
      >
        {EXCLUIR_EMPRESA}
      </button>
      {aberto ? (
        <ConfirmacaoDaExclusao
          empresaId={empresaId}
          nome={nomeExibidoDaEmpresa(ficha)}
          gestao={sessao.escopo_de_carteira === 'todas'}
          aoFechar={() => {
            setAberto(false)
          }}
        />
      ) : null}
    </section>
  )
}

interface PropriedadesDaConfirmacao {
  empresaId: string
  nome: string
  gestao: boolean
  aoFechar: () => void
}

/** O diálogo — montado só aberto, e é isso que faz a prévia ser lida a cada abertura. */
function ConfirmacaoDaExclusao({ empresaId, nome, gestao, aoFechar }: PropriedadesDaConfirmacao) {
  const previa = usePreviaDaExclusao(empresaId)
  const excluir = useExcluirEmpresa(empresaId)
  const esquecer = useEsquecerAEmpresa()
  const navegar = useNavigate()

  const lida: PreviaDaExclusao | undefined = previa.data
  const podeConfirmar = lida !== undefined && lida.bloqueio === null
  const erro = previa.isError ? previa.error : excluir.isError ? excluir.error : undefined

  return (
    <Dialogo
      titulo={EXCLUIR_EMPRESA}
      subtitulo={nome}
      rotuloDoConfirmar={excluir.isPending ? EXCLUINDO : SIM_EXCLUIR}
      semConfirmar={!podeConfirmar}
      ocupado={excluir.isPending}
      erro={erro}
      aoFechar={aoFechar}
      {...(podeConfirmar ? {} : { acaoSecundaria: { rotulo: FECHAR, aoAcionar: aoFechar } })}
      aoConfirmar={() => {
        excluir.mutate(undefined, {
          onSuccess: () => {
            //: Sai da ficha **antes** de esquecê-la: montada, ela releria a Empresa que saiu.
            void navegar({ to: CAMINHOS.empresas, replace: true }).then(() => {
              esquecer(empresaId)
              anunciar(EMPRESA_EXCLUIDA)
            })
          },
        })
      }}
    >
      {previa.isPending ? (
        <p className="exclusao-da-empresa__texto" role="status">
          {CONTANDO}
        </p>
      ) : lida === undefined ? null : lida.bloqueio !== null ? (
        //: A PA-5, verbatim: a mensagem é do serviço, e a tela não a reescreve.
        <p className="exclusao-da-empresa__bloqueio">{lida.bloqueio}</p>
      ) : (
        <>
          <p className="exclusao-da-empresa__pergunta">{perguntaDaExclusao(lida.negocios)}</p>
          {gestao && lida.em_andamento_de_outras_pessoas > 0 ? (
            <p className="exclusao-da-empresa__texto">
              {deOutrasPessoas(lida.em_andamento_de_outras_pessoas)}
            </p>
          ) : null}
          <p className="exclusao-da-empresa__texto">{AVISO_DA_EXCLUSAO}</p>
        </>
      )}
    </Dialogo>
  )
}
